import { useMemo, useState } from "react";
import { Scissors, Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function HamKaasSnijRegistratie({ xl }) {
  const { canEdit, kgf, euro, toNum, helpers, hamKaasPartijen, registerHamKaasSnij, verwijderHamKaasSnij, halffabRuns } = xl;
  const [form, setForm] = useState({
    datum: today(), hamNaam: "", kaasNaam: "", outputNaam: "",
    totaalKg: "", outputTrace: "", locatie: "Vriezer",
    hamChosen: [], kaasChosen: [],
  });
  const set = (patch) => setForm({ ...form, ...patch });

  const hamKaasHelpers = (helpers || []).filter((h) => h.type === "Ham/kaas");
  const hamPartijen = useMemo(() => (form.hamNaam && hamKaasPartijen ? hamKaasPartijen(form.hamNaam) : []), [form.hamNaam, hamKaasPartijen]);
  const kaasPartijen = useMemo(() => (form.kaasNaam && hamKaasPartijen ? hamKaasPartijen(form.kaasNaam) : []), [form.kaasNaam, hamKaasPartijen]);

  const helftKg = toNum(form.totaalKg) / 2;
  const hamGekozenRest = hamPartijen.filter((p) => form.hamChosen.includes(p.tracecode)).reduce((s, p) => s + toNum(p.rest), 0);
  const kaasGekozenRest = kaasPartijen.filter((p) => form.kaasChosen.includes(p.tracecode)).reduce((s, p) => s + toNum(p.rest), 0);

  const snijRuns = (halffabRuns || []).filter((r) => r && r.soort === "hamkaas-snij").sort((a, b) => String(b.datum).localeCompare(String(a.datum)));

  const defaultTrace = `HKS-${String(form.datum || "").replace(/-/g, "")}`;
  const submit = () => {
    const ok = registerHamKaasSnij({
      datum: form.datum, hamNaam: form.hamNaam, kaasNaam: form.kaasNaam, outputNaam: form.outputNaam,
      totaalKg: toNum(form.totaalKg), outputTrace: String(form.outputTrace || defaultTrace).trim(), locatie: form.locatie,
      hamPartijen: hamPartijen.filter((p) => form.hamChosen.includes(p.tracecode)),
      kaasPartijen: kaasPartijen.filter((p) => form.kaasChosen.includes(p.tracecode)),
    });
    if (ok) setForm({ datum: form.datum, hamNaam: "", kaasNaam: "", outputNaam: "", totaalKg: "", outputTrace: "", locatie: form.locatie, hamChosen: [], kaasChosen: [] });
  };

  const inp = "mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-cyan-600 focus:outline-none";
  const lbl = "flex flex-col text-xs font-semibold text-slate-600";

  const Partijen = ({ partijen, chosen, onToggle, testid, kleur }) => (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {partijen.length === 0 ? <span className="text-xs text-slate-400">Geen partijen met voorraad.</span> : partijen.map((p) => {
        const on = chosen.includes(p.tracecode);
        return (
          <button type="button" key={p.tracecode} data-testid={`${testid}-opt`} onClick={() => onToggle(p.tracecode)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? `border-${kleur}-600 bg-${kleur}-600 text-white` : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"}`}
            style={on ? { backgroundColor: kleur === "violet" ? "#6d28d9" : "#0e7490", borderColor: kleur === "violet" ? "#6d28d9" : "#0e7490", color: "#fff" } : undefined}>
            {p.tracecode} · {kgf(p.rest)} {p.datum ? `· ${p.datum}` : ""}
          </button>
        );
      })}
    </div>
  );

  return (
    <details className="mb-6 rounded-lg border border-violet-200 bg-violet-50/60 p-4" data-testid="hk-snij">
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3" data-testid="hk-snij-toggle">
        <span className="flex items-center gap-2 text-lg font-bold text-slate-900"><Scissors className="h-5 w-5 text-violet-700" />Ham/kaas snijden — partijen kiezen → 50/50 → nieuwe invriespartij</span>
        <span className="text-sm font-medium text-violet-800">{snijRuns.length} registratie{snijRuns.length === 1 ? "" : "s"} · openklappen</span>
      </summary>

      <div className="mt-3">
        <p className="mb-3 max-w-4xl text-sm text-slate-600">
          Kies de <b>ham- en kaaspartijen</b> (tracecodes met voorraad) die je snijdt en vul het <b>totaal gesneden kg</b> in.
          Het systeem verdeelt dit <b>50% ham / 50% kaas</b>, boekt FIFO af van de gekozen partijen en maakt één nieuwe invriespartij met een
          gecombineerde tracecode die naar <b>beide</b> bronpartijen verwijst (apart terug te traceren in de recall). Volledig op je bestaande voorraadmotor; terug te draaien.
        </p>

        {canEdit && (
          <div className="rounded-lg border border-violet-100 bg-white p-3">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <label className={lbl}>Snijdatum
                <input type="date" data-testid="hk-snij-datum" className={inp} value={form.datum} onChange={(e) => set({ datum: e.target.value })} />
              </label>
              <label className={lbl}>Ham — artikel
                <select data-testid="hk-snij-hamnaam" className={inp} value={form.hamNaam} onChange={(e) => set({ hamNaam: e.target.value, hamChosen: [] })}>
                  <option value="">— kies ham —</option>
                  {hamKaasHelpers.map((h) => <option key={h.id || h.naam} value={h.naam}>{h.naam}</option>)}
                </select>
              </label>
              <label className={lbl}>Kaas — artikel
                <select data-testid="hk-snij-kaasnaam" className={inp} value={form.kaasNaam} onChange={(e) => set({ kaasNaam: e.target.value, kaasChosen: [] })}>
                  <option value="">— kies kaas —</option>
                  {hamKaasHelpers.map((h) => <option key={h.id || h.naam} value={h.naam}>{h.naam}</option>)}
                </select>
              </label>
              <label className={lbl}>Totaal gesneden kg
                <input type="number" step="any" data-testid="hk-snij-totaalkg" className={inp} value={form.totaalKg} onChange={(e) => set({ totaalKg: e.target.value })} placeholder="bv. 40" />
              </label>
            </div>

            {form.hamNaam && (
              <div className="mt-3">
                <div className="mb-1 text-xs font-semibold text-violet-700">Hampartijen — kies tracecode(s) {helftKg > 0 ? `· nodig: ${kgf(helftKg)} · gekozen voorraad: ${kgf(hamGekozenRest)}` : ""}</div>
                <Partijen partijen={hamPartijen} chosen={form.hamChosen} testid="hk-snij-hampartijen" kleur="violet"
                  onToggle={(t) => set({ hamChosen: form.hamChosen.includes(t) ? form.hamChosen.filter((x) => x !== t) : [...form.hamChosen, t] })} />
              </div>
            )}
            {form.kaasNaam && (
              <div className="mt-3">
                <div className="mb-1 text-xs font-semibold text-cyan-700">Kaaspartijen — kies tracecode(s) {helftKg > 0 ? `· nodig: ${kgf(helftKg)} · gekozen voorraad: ${kgf(kaasGekozenRest)}` : ""}</div>
                <Partijen partijen={kaasPartijen} chosen={form.kaasChosen} testid="hk-snij-kaaspartijen" kleur="cyan"
                  onToggle={(t) => set({ kaasChosen: form.kaasChosen.includes(t) ? form.kaasChosen.filter((x) => x !== t) : [...form.kaasChosen, t] })} />
              </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-3 rounded-md bg-violet-50 p-3 md:grid-cols-3">
              <label className={lbl}>Eindartikel (ham/kaas)
                <select data-testid="hk-snij-outputnaam" className={inp} value={form.outputNaam} onChange={(e) => set({ outputNaam: e.target.value })}>
                  <option value="">— kies ham/kaas —</option>
                  {hamKaasHelpers.map((h) => <option key={h.id || h.naam} value={h.naam}>{h.naam}</option>)}
                </select>
              </label>
              <label className={lbl}>Nieuwe gecombineerde tracecode
                <input data-testid="hk-snij-outputtrace" className={inp} value={form.outputTrace} onChange={(e) => set({ outputTrace: e.target.value })} placeholder={defaultTrace} />
              </label>
              <label className={lbl}>Locatie
                <input data-testid="hk-snij-locatie" className={inp} value={form.locatie} onChange={(e) => set({ locatie: e.target.value })} />
              </label>
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button data-testid="hk-snij-add" onClick={submit} className="rounded-md bg-violet-700 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-800">Snijden registreren</button>
              {helftKg > 0 && <span className="text-xs text-slate-500">→ {kgf(helftKg)} ham + {kgf(helftKg)} kaas</span>}
            </div>
          </div>
        )}

        <div className="mt-4 overflow-x-auto rounded-lg border border-violet-100 bg-white">
          <table className="w-full text-sm" data-testid="hk-snij-table">
            <thead className="bg-violet-50 text-left text-xs uppercase text-slate-500">
              <tr><th className="px-3 py-2">Datum</th><th className="px-3 py-2">Eindartikel</th><th className="px-3 py-2">Afgeboekt (ham + kaas, per tracecode)</th><th className="px-3 py-2 text-right">Kostprijs/kg</th>{canEdit && <th className="px-3 py-2" />}</tr>
            </thead>
            <tbody>
              {snijRuns.length === 0 ? (
                <tr><td colSpan={canEdit ? 5 : 4} className="px-3 py-6 text-center text-slate-400">Nog geen snij-registraties.</td></tr>
              ) : snijRuns.map((r) => (
                <tr key={r.id} data-testid="hk-snij-row" className="border-t border-slate-100">
                  <td className="px-3 py-2">{r.datum}</td>
                  <td className="px-3 py-2 font-medium">{String(r.recept).replace(" · snijden", "")}</td>
                  <td className="px-3 py-2 text-xs">{(r.verbruik || []).map((v) => `${kgf(v.kg)} ${v.naam}${v.eigenTracecode ? ` [${v.eigenTracecode}]` : ""}`).join(" + ")}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{euro(toNum(r.kostprijsKg))}</td>
                  {canEdit && <td className="px-3 py-2 text-right"><button data-testid="hk-snij-del" onClick={() => { if (window.confirm("Deze snij-registratie terugdraaien? Ham, kaas en invriespartij worden hersteld.")) verwijderHamKaasSnij(r.snijId || r.id); }} className="text-rose-600 hover:text-rose-800"><Trash2 className="h-4 w-4" /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}
